import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DocumentUpload from '../../common/DocumentUpload';
import { FormService } from '../../../services/patientmr/forms.service';
import type { FormRecordDto, FormDocumentDto } from '../../../dtos/patientmr/forms.dto';
import { useAuth } from '../../../context/authcontext';
import { usePatient } from '../../../context/patientContext';
import { fetchDemographics } from '../../../services/patientmr/demographics.service';
import { getAgencyByTenantId, formatAgencyZip } from '../../../services/admin/agency.service';
import PrintedNameField from './PrintedNameField';

interface TXTherapiesPAFormProps {
  isReadOnly?: boolean;
  initialData?: Record<string, any>;
  onDataChange?: (data: any) => void;
  patientId?: string;
  formRecord?: FormRecordDto;
  onFormRecordChange?: (record: FormRecordDto) => void;
}

const CollapsibleSection: React.FC<{
  title: string;
  collapsed: boolean; onToggle: () => void;
  children: React.ReactNode; colorClass?: string;
}> = ({ title, collapsed, onToggle, children, colorClass = 'border-slate-200 bg-slate-50' }) => (
  <div className={`rounded-lg border ${colorClass} shadow-sm mb-4`}>
    <div className="flex items-center justify-between cursor-pointer px-4 py-3 hover:bg-slate-100 rounded-t-lg transition-colors" onClick={onToggle}>
      <h4 className="text-base font-semibold text-gray-900 text-left">{title}</h4>
      {collapsed ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronUp size={16} className="text-gray-500 shrink-0" />}
    </div>
    <div className={`px-4 pb-4 pt-2 ${collapsed ? 'hidden' : 'block'}`}>{children}</div>
  </div>
);

const RequiredMark: React.FC = () => <span className="text-red-500 ml-0.5">*</span>;

const inputClass =
  'w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm text-left';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 text-left';

const TXTherapiesPAForm: React.FC<TXTherapiesPAFormProps> = ({ isReadOnly = false, initialData = {}, onDataChange, patientId, formRecord, onFormRecordChange }) => {
  const { user } = useAuth();
  const { selectedPatient } = usePatient();

  const [form, setForm] = useState<Record<string, any>>({
    weAgree: false,
    clientName: '',
    medicaidNumber: '',
    dateOfBirth: '',
    conditionType: '',
    treatmentDiagnoses: '',
    medicalDiagnoses: '',
    placeOfServiceOffice: false,
    placeOfServiceOutpatient: false,
    placeOfServiceHome: false,
    placeOfServiceOther: false,
    placeOfServiceOtherText: '',
    lastEvalPT: '',
    lastEvalOT: '',
    lastEvalST: '',
    ptDosFrom: '',
    ptDosThrough: '',
    ptFrequency: '',
    ptUnits: '',
    otDosFrom: '',
    otDosThrough: '',
    otFrequency: '',
    otUnits: '',
    stDosFrom: '',
    stDosThrough: '',
    stFrequency: '',
    stUnits: '',
    frequencyNotes: '',
    procedureCodes: '',
    physicalTherapistName: '',
    occupationalTherapistName: '',
    speechTherapistName: '',
    requestingProviderName: '',
    requestingProviderNpiLicense: '',
    dateClientLastSeen: '',
    billingProviderName: '',
    billingProviderPhone: '',
    billingProviderFax: '',
    billingProviderAddress: '',
    billingProviderCity: '',
    billingProviderState: '',
    billingProviderZip: '',
    billingProviderTaxId: '',
    billingProviderNpi: '',
    billingProviderTaxonomy: '',
    billingProviderBenefitCode: '',
    ...initialData,
  });

  useEffect(() => {
    if (formRecord !== undefined || !patientId) return;

    const autoPopulate = async () => {
      try {
        const [demographics, agency] = await Promise.all([
          fetchDemographics(patientId).catch(() => null),
          user?.tenantId ? getAgencyByTenantId(user.tenantId).catch(() => null) : Promise.resolve(null),
        ]);

        const updates: Record<string, any> = {};

        if (selectedPatient?.firstName && selectedPatient?.lastName) {
          updates.clientName = `${selectedPatient.lastName}, ${selectedPatient.firstName}`;
        } else if (selectedPatient?.lastName) {
          updates.clientName = selectedPatient.lastName;
        } else if (selectedPatient?.firstName) {
          updates.clientName = selectedPatient.firstName;
        }

        if (selectedPatient?.dob) updates.dateOfBirth = selectedPatient.dob;

        const medicaidId = demographics?.medicaidID || demographics?.medicaidId;
        if (medicaidId) updates.medicaidNumber = medicaidId;

        if (agency?.legalName) updates.billingProviderName = agency.legalName;
        if (agency?.contactInfo?.officePhone) updates.billingProviderPhone = agency.contactInfo.officePhone;
        if (agency?.contactInfo?.fax) updates.billingProviderFax = agency.contactInfo.fax;
        if (agency?.address?.street) updates.billingProviderAddress = agency.address.street;
        if (agency?.address?.city) updates.billingProviderCity = agency.address.city;
        if (agency?.address?.state) updates.billingProviderState = agency.address.state;
        if (agency?.address?.zipCode) updates.billingProviderZip = formatAgencyZip(agency);
        if (agency?.taxId) updates.billingProviderTaxId = agency.taxId;
        if (agency?.npi) updates.billingProviderNpi = agency.npi;
        if (agency?.taxonomyCode) updates.billingProviderTaxonomy = agency.taxonomyCode;
        if (agency?.benefitCode) updates.billingProviderBenefitCode = agency.benefitCode;

        setForm(prev => ({ ...prev, ...updates }));
      } catch (err) {
        console.error('[TXTherapiesPAForm] Auto-populate failed:', err);
      }
    };

    autoPopulate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (k: string) => setCollapsedSections(p => ({ ...p, [k]: !p[k] }));

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);


  const handleFilesSelected = (files: File[]) => {
    if (formRecord?.status !== 'COMPLETE') {
      setActionError('Document upload is only available when the form is in Complete status.');
      return;
    }
    setActionError(null);
    setStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadStagedFiles = async () => {
    if (!stagedFiles.length || !patientId || !formRecord) return;
    setActionError(null);
    setUploading(true);
    try {
      let updatedForm = formRecord;
      for (const file of stagedFiles) {
        updatedForm = await FormService.addDocument(patientId, formRecord.formId, file);
      }
      setStagedFiles([]);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Upload failed:', err);
      setActionError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      const result = await FormService.getDocumentUrl(patientId, formRecord.formId, doc.id);
      window.open(result.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      setActionError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      setDeletingDocId(doc.id);
      const updatedForm = await FormService.deleteDocument(patientId, formRecord.formId, doc.id);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Delete failed:', err);
      setActionError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const set = (field: string, value: any) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 text-left">
      {/* Form Title */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide text-left">Form Code: F00139</p>
        <p className="text-sm text-gray-600 text-left">Texas Medicaid Physical, Occupational, or Speech Therapy Prior Authorization Request</p>
      </div>

      {/* Section 1 — Certification */}
      <CollapsibleSection title="Section 1: Prior Authorization Request Submitter Certification" collapsed={!!collapsedSections['certification']} onToggle={() => toggleSection('certification')} colorClass="border-slate-200 bg-slate-50">
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!form.weAgree}
            onChange={e => set('weAgree', e.target.checked)}
            disabled={isReadOnly}
            className="mt-0.5 rounded border-gray-300"
          />
          <span>We Agree — I certify that the Prior Authorization Request Submitter Certification Statement is true and accurate.</span>
        </label>
      </CollapsibleSection>

      {/* Section 2 — Client Information */}
      <CollapsibleSection title="Section 2: Client Information" collapsed={!!collapsedSections['client-info']} onToggle={() => toggleSection('client-info')} colorClass="border-blue-200 bg-blue-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Client Name (Last, First, M.I.)<RequiredMark /></label>
            <input type="text" value={form.clientName} onChange={e => set('clientName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Medicaid Number<RequiredMark /></label>
            <input type="text" value={form.medicaidNumber} onChange={e => set('medicaidNumber', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date of Birth<RequiredMark /></label>
            <input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Condition Type</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" name="conditionType" value="acute" checked={form.conditionType === 'acute'} onChange={e => set('conditionType', e.target.value)} disabled={isReadOnly} className="border-gray-300" />
                Acute (up to 60-day authorization)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" name="conditionType" value="chronic" checked={form.conditionType === 'chronic'} onChange={e => set('conditionType', e.target.value)} disabled={isReadOnly} className="border-gray-300" />
                Chronic (up to 180-day authorization)
              </label>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className={labelClass}>Treatment Diagnoses</label>
            <textarea rows={3} value={form.treatmentDiagnoses} onChange={e => set('treatmentDiagnoses', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Medical Diagnoses</label>
            <textarea rows={3} value={form.medicalDiagnoses} onChange={e => set('medicalDiagnoses', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 3 — Place of Service */}
      <CollapsibleSection title="Section 3: Place of Service" collapsed={!!collapsedSections['place-of-service']} onToggle={() => toggleSection('place-of-service')} colorClass="border-indigo-200 bg-indigo-50">
        <div className="flex flex-wrap gap-4 mb-2">
          {[
            { field: 'placeOfServiceOffice', label: 'Office' },
            { field: 'placeOfServiceOutpatient', label: 'Outpatient' },
            { field: 'placeOfServiceHome', label: 'Home' },
            { field: 'placeOfServiceOther', label: 'Other' },
          ].map(({ field, label }) => (
            <label key={field} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form[field]} onChange={e => set(field, e.target.checked)} disabled={isReadOnly} className="rounded border-gray-300" />
              {label}
            </label>
          ))}
        </div>
        {form.placeOfServiceOther && (
          <div className="mt-2">
            <label className={labelClass}>Other (specify)</label>
            <input type="text" value={form.placeOfServiceOtherText} onChange={e => set('placeOfServiceOtherText', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        )}
      </CollapsibleSection>

      {/* Section 4 — Last Therapy Evaluation / Re-Evaluation */}
      <CollapsibleSection title="Section 4: Last Therapy Evaluation / Re-Evaluation" collapsed={!!collapsedSections['last-eval']} onToggle={() => toggleSection('last-eval')} colorClass="border-violet-200 bg-violet-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>PT Last Eval/Re-Eval Date</label>
            <input type="date" value={form.lastEvalPT} onChange={e => set('lastEvalPT', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>OT Last Eval/Re-Eval Date</label>
            <input type="date" value={form.lastEvalOT} onChange={e => set('lastEvalOT', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>ST Last Eval/Re-Eval Date</label>
            <input type="date" value={form.lastEvalST} onChange={e => set('lastEvalST', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 5 — Discipline Requests Table */}
      <CollapsibleSection title="Section 5: Discipline Requests" collapsed={!!collapsedSections['discipline-requests']} onToggle={() => toggleSection('discipline-requests')} colorClass="border-green-200 bg-green-50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200 rounded-md">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-3 py-2 font-medium text-gray-700 border-b border-slate-200">Discipline</th>
                <th className="px-3 py-2 font-medium text-gray-700 border-b border-slate-200">DOS From</th>
                <th className="px-3 py-2 font-medium text-gray-700 border-b border-slate-200">DOS Through</th>
                <th className="px-3 py-2 font-medium text-gray-700 border-b border-slate-200">Frequency</th>
                <th className="px-3 py-2 font-medium text-gray-700 border-b border-slate-200">Units</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'PT (GP)', fromField: 'ptDosFrom', throughField: 'ptDosThrough', freqField: 'ptFrequency', unitsField: 'ptUnits' },
                { label: 'OT (GO)', fromField: 'otDosFrom', throughField: 'otDosThrough', freqField: 'otFrequency', unitsField: 'otUnits' },
                { label: 'ST (GN)', fromField: 'stDosFrom', throughField: 'stDosThrough', freqField: 'stFrequency', unitsField: 'stUnits' },
              ].map(row => (
                <tr key={row.label} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-medium text-gray-700">{row.label}</td>
                  <td className="px-2 py-1">
                    <input type="date" value={form[row.fromField] || ''} onChange={e => set(row.fromField, e.target.value)} readOnly={isReadOnly} className={inputClass} />
                  </td>
                  <td className="px-2 py-1">
                    <input type="date" value={form[row.throughField] || ''} onChange={e => set(row.throughField, e.target.value)} readOnly={isReadOnly} className={inputClass} />
                  </td>
                  <td className="px-2 py-1">
                    <input type="text" value={form[row.freqField] || ''} onChange={e => set(row.freqField, e.target.value)} readOnly={isReadOnly} className={inputClass} />
                  </td>
                  <td className="px-2 py-1">
                    <input type="text" value={form[row.unitsField] || ''} onChange={e => set(row.unitsField, e.target.value)} readOnly={isReadOnly} className={inputClass} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Frequency Notes (taper/variable frequency or discharge date)</label>
            <textarea rows={2} value={form.frequencyNotes} onChange={e => set('frequencyNotes', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Procedure Codes<RequiredMark /></label>
            <input type="text" value={form.procedureCodes} onChange={e => set('procedureCodes', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 6 — Specialist Signatures */}
      <CollapsibleSection title="Section 6: Specialist Signatures" collapsed={!!collapsedSections['specialist-sigs']} onToggle={() => toggleSection('specialist-sigs')} colorClass="border-amber-200 bg-amber-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <PrintedNameField signer="Physical Therapist" value={form.physicalTherapistName} onChange={v => set('physicalTherapistName', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <PrintedNameField signer="Occupational Therapist" value={form.occupationalTherapistName} onChange={v => set('occupationalTherapistName', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <PrintedNameField signer="Speech Therapist" value={form.speechTherapistName} onChange={v => set('speechTherapistName', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 7 — Requesting Provider */}
      <CollapsibleSection title="Section 7: Requesting Provider" collapsed={!!collapsedSections['requesting-provider']} onToggle={() => toggleSection('requesting-provider')} colorClass="border-teal-200 bg-teal-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Requesting Provider Name<RequiredMark /></label>
            <input type="text" value={form.requestingProviderName} onChange={e => set('requestingProviderName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>NPI / License<RequiredMark /></label>
            <input type="text" value={form.requestingProviderNpiLicense} onChange={e => set('requestingProviderNpiLicense', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date Client Last Seen</label>
            <input type="date" value={form.dateClientLastSeen} onChange={e => set('dateClientLastSeen', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 8 — Rendering/Billing Provider */}
      <CollapsibleSection title="Section 8: Rendering / Billing Provider" collapsed={!!collapsedSections['billing-provider']} onToggle={() => toggleSection('billing-provider')} colorClass="border-rose-200 bg-rose-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Provider Name<RequiredMark /></label>
            <input type="text" value={form.billingProviderName} onChange={e => set('billingProviderName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Telephone</label>
            <input type="tel" value={form.billingProviderPhone} onChange={e => set('billingProviderPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fax</label>
            <input type="tel" value={form.billingProviderFax} onChange={e => set('billingProviderFax', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Street Address<RequiredMark /></label>
            <input type="text" value={form.billingProviderAddress} onChange={e => set('billingProviderAddress', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>City</label>
            <input type="text" value={form.billingProviderCity} onChange={e => set('billingProviderCity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>State</label>
              <input type="text" maxLength={2} value={form.billingProviderState} onChange={e => set('billingProviderState', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>ZIP+4<RequiredMark /></label>
              <input type="text" value={form.billingProviderZip} onChange={e => set('billingProviderZip', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Tax ID<RequiredMark /></label>
            <input type="text" value={form.billingProviderTaxId} onChange={e => set('billingProviderTaxId', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>NPI<RequiredMark /></label>
            <input type="text" value={form.billingProviderNpi} onChange={e => set('billingProviderNpi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Taxonomy<RequiredMark /></label>
            <input type="text" value={form.billingProviderTaxonomy} onChange={e => set('billingProviderTaxonomy', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Benefit Code<RequiredMark /></label>
            <input type="text" value={form.billingProviderBenefitCode} onChange={e => set('billingProviderBenefitCode', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Generate PDF moved to page action bar */}

      {actionError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex justify-between items-start">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-2 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Documents */}
      {formRecord && (
        <>
          <DocumentUpload
            documents={(formRecord.documents ?? []).map(d => ({
              id: d.id,
              name: d.name,
              size: d.size,
              uploadedAt: d.uploadedAt,
            }))}
            stagedFiles={stagedFiles}
            canUpload={formRecord.status === 'COMPLETE'}
            onFilesSelected={handleFilesSelected}
            onStagedRemove={handleRemoveStagedFile}
            onDownload={handleDownloadDocument}
            onDelete={handleDeleteDocument}
            uploading={uploading}
            deletingId={deletingDocId}
            collapsible={true}
            defaultOpen={true}
            maxSizeMb={7}
          />
          {stagedFiles.length > 0 && formRecord.status === 'COMPLETE' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUploadStagedFiles}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TXTherapiesPAForm;
